import { ChannelsProductsSidebarList_NavSectionPaginationFragment$key } from "@/chat/sidebar/channels/__generated__/ChannelsProductsSidebarList_NavSectionPaginationFragment.graphql"
import { ChannelsProductsSidebarList_NavSectionPaginationQuery } from "@/chat/sidebar/channels/__generated__/ChannelsProductsSidebarList_NavSectionPaginationQuery.graphql"
import { ChannelsProductsSidebarList_OrganizationPaginationFragment$key } from "@/chat/sidebar/channels/__generated__/ChannelsProductsSidebarList_OrganizationPaginationFragment.graphql"
import { ChannelsProductsSidebarList_OrganizationPaginationQuery } from "@/chat/sidebar/channels/__generated__/ChannelsProductsSidebarList_OrganizationPaginationQuery.graphql"
import ChannelsSidebarProduct from "@/chat/sidebar/channels/ChannelsSidebarProduct"
import RenderIfVisible from "@/product/sidebar/RenderIfVisible"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoDivider, DiscoSideBarItemSkeleton } from "@disco-ui"
import DiscoScrolledIntoView from "@disco-ui/scrolled-into-view/DiscoScrolledIntoView"
import { range } from "@utils/array/arrayUtils"
import useFeatureFlags from "@utils/hook/useFeatureFlags"
import { TestIDProps } from "@utils/typeUtils"
import { graphql, usePaginationFragment } from "react-relay"

type Props = TestIDProps & {
  topLevelKey?: ChannelsProductsSidebarList_OrganizationPaginationFragment$key | null
  navSectionKey?: ChannelsProductsSidebarList_NavSectionPaginationFragment$key | null
}

const PRODUCTS_PER_PAGE = 10

export default function ChannelsProductsSidebarList(props: Props) {
  const { topLevelKey, navSectionKey, testid = "ChannelsProductsSidebarList" } = props
  const classes = useStyles()
  const { virtualizeProductsSidebar } = useFeatureFlags()

  // Top level products with their pagination
  const topLevelPagination = usePaginationFragment<
    ChannelsProductsSidebarList_OrganizationPaginationQuery,
    ChannelsProductsSidebarList_OrganizationPaginationFragment$key
  >(
    graphql`
      fragment ChannelsProductsSidebarList_OrganizationPaginationFragment on Query
      @refetchable(queryName: "ChannelsProductsSidebarList_OrganizationPaginationQuery")
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 10 }
        after: { type: "String" }
      ) {
        topLevelProducts: channelsSidebarTopLevelProducts(first: $first, after: $after)
          @connection(
            key: "ChannelsProductsSidebarList_OrganizationPaginationFragment__topLevelProducts"
          ) {
          edges {
            node {
              id
              ...ChannelsSidebarProductFragment
            }
          }
        }
      }
    `,
    topLevelKey || null
  )
  const organization = topLevelPagination?.data

  // Nav section level products with their pagination
  const navSectionPagination = usePaginationFragment<
    ChannelsProductsSidebarList_NavSectionPaginationQuery,
    ChannelsProductsSidebarList_NavSectionPaginationFragment$key
  >(
    graphql`
      fragment ChannelsProductsSidebarList_NavSectionPaginationFragment on NavSection
      @refetchable(queryName: "ChannelsProductsSidebarList_NavSectionPaginationQuery")
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 10 }
        after: { type: "String" }
      ) {
        id
        products: channelsSidebarNavSectionProducts(first: $first, after: $after)
          @connection(
            key: "ChannelsProductsSidebarList_NavSectionPaginationFragment__products"
          ) {
          edges {
            node {
              id
              ...ChannelsSidebarProductFragment
            }
          }
        }
      }
    `,
    navSectionKey || null
  )
  const navSection = navSectionPagination?.data

  // Use either the organization or nav section fragment's pagination + data
  const { loadNext, hasNext, isLoadingNext } = organization
    ? topLevelPagination
    : navSectionPagination
  const products = organization
    ? Relay.connectionToArray(organization.topLevelProducts)
    : Relay.connectionToArray(navSection?.products)

  if (!products.length) return null

  return (
    <>
      {topLevelKey && <DiscoDivider marginTop={1.5} marginBottom={1.5} />}

      <div className={classes.list} data-testid={testid}>
        {products.map((product, i) => {
          const content = (
            <ChannelsSidebarProduct
              key={product.id}
              testid={`${testid}.${i}`}
              productKey={product}
            />
          )

          if (virtualizeProductsSidebar) {
            return <RenderIfVisible key={product.id}>{content}</RenderIfVisible>
          }

          return content
        })}

        {hasNext && (
          <DiscoScrolledIntoView
            onScrolledIntoView={() => loadNext(PRODUCTS_PER_PAGE)}
            isLoading={isLoadingNext}
            skeleton={
              <>
                {range(PRODUCTS_PER_PAGE).map((k) => (
                  <DiscoSideBarItemSkeleton key={k} />
                ))}
              </>
            }
          />
        )}
      </div>
    </>
  )
}

const useStyles = makeUseStyles((theme) => ({
  list: {
    padding: theme.spacing(0, 2),
  },
}))
